import { Component, inject, signal, WritableSignal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import Keycloak from 'keycloak-js';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { UserInformation } from './model/keycloak/user-information.model';
import { PackageService } from './services/package.service';
import { MatDivider } from '@angular/material/divider';
import { UserService } from './services/user.service';
import { UserInformation as ApiUserInformation } from './model/user-information.model';
import { ClaimRequestService } from './services/claim-request.service';
import { PaginatedResponse } from './model/pagination/paginated-response.model';
import { OwnershipClaim } from './model/ownership-claim/ownership-claim.model';
import { OwnershipClaimStatus } from './model/ownership-claim/ownership-claim-status.enum';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatToolbar,
    MatIcon,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    RouterLink,
    RouterLinkActive,
    MatDivider,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly keycloak: Keycloak = inject(Keycloak);
  public readonly packageService = inject(PackageService);
  public userInformation: UserInformation = new UserInformation();
  private userService = inject(UserService);
  private claimRequestService = inject(ClaimRequestService);
  // Back-end user, which decides what the toolbar is allowed to show
  public user: WritableSignal<ApiUserInformation> = signal(new ApiUserInformation());
  // Amount of claim requests that are still waiting to be attended
  public openClaimRequestCount: WritableSignal<number> = signal(0);

  constructor() {
    this.loadUserInformation();
    this.loadUser();
  }

  private loadUserInformation() {
    this.keycloak.loadUserInfo().then(userInfo => {
      this.userInformation = UserInformation.createFromApi(userInfo);
    });
  }

  private loadUser(): void {
    this.userService.getUserInformation().subscribe({
      next: (user: ApiUserInformation): void => {
        this.user.set(user);
        // Claim requests are an admin-only screen, so only admins need the counter
        if (user.isAdmin) {
          this.loadOpenClaimRequestCount();
        }
      },
      // Falling back to the default user hides everything that needs admin rights
      error: (): void => this.user.set(new ApiUserInformation()),
    });
  }

  /**
   * Only the total is needed, so ask for the smallest possible page
   */
  private loadOpenClaimRequestCount(): void {
    this.claimRequestService.getPaginatedList({
      params: {
        page: 1,
        pageSize: 1,
        status: OwnershipClaimStatus.open,
      },
    }).subscribe({
      next: (response: PaginatedResponse<OwnershipClaim>): void => {
        this.openClaimRequestCount.set(response.total);
      },
      error: (): void => {
        this.openClaimRequestCount.set(0);
      },
    });
  }

  public logOut(): void {
    this.keycloak.logout();
  }
}
